import { cva, type VariantProps } from 'class-variance-authority';
import type React from 'react';
import type { ComponentProps, ComponentPropsWithoutRef, Ref } from 'react';
import { useCallback, useId, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { Observable } from 'rxjs';
import { cn } from '../../common/functions/cn';
import { Button, type ButtonProps } from '../button/button';
import OdinIcon from '../odin-icon/odin-icon.component';
import { Spinner } from '../spinner/spinner';

export const dialogBaseStyles = cn(
  'scale-95 opacity-0 transition-[opacity,scale,overlay,display] transition-discrete duration-100 ease-out motion-reduce:transition-none',
  'open:scale-100 starting:open:scale-95 open:opacity-100 starting:open:opacity-0',
  'backdrop:opacity-0 backdrop:transition-[opacity,overlay,display] backdrop:transition-discrete backdrop:duration-100 backdrop:ease-out motion-reduce:backdrop:transition-none',
  'backdrop:bg-black/60 open:backdrop:opacity-100 starting:open:backdrop:opacity-0'
);

export interface ModalDialogButtonSetting extends ButtonProps {
  name: string;
  text: string;
  closeModalOnClick?: boolean;
}

interface DialogResult {
  buttonName: string | undefined;
}

export interface DialogRef {
  show: () => void;
  close: () => void;
  subscribeToDialogResult?: () => Observable<DialogResult> | undefined;
}

const dialogVariants = cva(
  [
    'm-auto flex-col gap-4 overflow-auto p-4 open:flex',
    // The UA caps a dialog 6px tighter than a 1rem inset, so the bounds are set rather than left to it.
    'max-h-[calc(100%-2rem)] w-[calc(100%-2rem)]',
    'rounded-md bg-popover text-popover-foreground text-sm outline-hidden ring-1 ring-border/80 [&:not([open])]:pointer-events-none',
    'has-data-[slot=dialog-close-icon]:*:data-[slot=dialog-header]:pr-8',
    dialogBaseStyles,
  ],
  {
    variants: {
      // Every preset keeps the inset in its own clamp, so a call site overriding the width still cannot overflow.
      size: {
        sm: 'max-w-[min(24rem,100%-2rem)]',
        md: 'max-w-[min(32rem,100%-2rem)]',
        lg: 'max-w-[min(48rem,100%-2rem)]',
        xl: 'max-w-[min(64rem,100%-2rem)]',
        full: 'h-[calc(100%-2rem)] max-w-[calc(100%-2rem)]',
      },
    },
    defaultVariants: {
      size: 'sm',
    },
  }
);

export interface DialogProps extends Omit<ComponentPropsWithoutRef<'dialog'>, 'onClose'> {
  buttons?: Array<ModalDialogButtonSetting>;
  description?: string;
  /** Names the dialog via `aria-labelledby`, and reserves the row the close icon floats over. */
  header: string;
  loading?: boolean;
  ref?: Ref<DialogRef>;
  /** Defaults to `showCloseIcon`. Set false to keep the icon but drop the footer cancel button. */
  showCloseButton?: boolean;
  /** The X floating in the top corner. Also the default for `showCloseButton`, so turning it off drops both. */
  showCloseIcon?: boolean;
  /** Width preset: 24rem, 32rem, 48rem, 64rem, or `full` to fill the viewport inside the standard 1rem inset. */
  size?: VariantProps<typeof dialogVariants>['size'];
  visible?: boolean;
  onClose?: (buttonName?: string) => void;
}

const DialogCloseButton = ({ className, ...props }: ButtonProps) => {
  const { t } = useTranslation();

  return (
    <Button
      data-slot="dialog-close-icon"
      variant="ghost"
      size="icon-sm"
      className={cn('absolute top-2 right-2', className)}
      {...props}
    >
      <OdinIcon icon="X" />
      <span className="sr-only">{t(($) => $.close)}</span>
    </Button>
  );
};

const DialogTitle = ({ id, className, ...props }: ComponentProps<'h2'> & { id: string }) => (
  <h2 data-slot="dialog-title" id={id} className={cn('font-medium text-base leading-none', className)} {...props} />
);

const DialogDescription = ({ id, className, ...props }: ComponentProps<'p'> & { id: string }) => (
  <p data-slot="dialog-description" id={id} className={cn('text-muted-foreground text-sm', className)} {...props} />
);

const DialogHeader = ({ className, ...props }: ComponentProps<'div'>) => (
  <div data-slot="dialog-header" className={cn('flex flex-col gap-2', className)} {...props} />
);

const DialogScrollArea = ({ className, ...props }: ComponentProps<'div'>) => (
  <div
    data-slot="dialog-scroll-area"
    className={cn('relative -mx-4 -my-2 min-h-0 flex-auto overflow-y-auto px-4 py-2', className)}
    {...props}
  />
);

interface DialogFooterProps {
  buttons?: Array<ModalDialogButtonSetting>;
  showCloseButton: boolean;
  onClose: (buttonName?: string) => void;
}

const DialogFooter = ({
  buttons,
  showCloseButton,
  onClose,
  className,
  ...props
}: DialogFooterProps & Omit<ComponentProps<'div'>, 'onClose'>) => {
  const { t } = useTranslation();

  if (!buttons?.length && !showCloseButton) return null;

  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        '-mx-4 -mb-4 flex shrink-0 flex-wrap items-center justify-end gap-2 rounded-b-md border-t border-solid bg-popover p-4',
        'max-sm:[@media(min-height:30rem)]:flex-col-reverse max-sm:[@media(min-height:30rem)]:items-stretch',
        className
      )}
      {...props}
    >
      {showCloseButton && (
        <Button variant="outline" onClick={() => onClose('cancel')}>
          {t(($) => $.cancel)}
        </Button>
      )}
      {buttons?.map(({ text, closeModalOnClick, onClick, ...buttonProps }) => (
        <Button
          key={buttonProps.name}
          {...buttonProps}
          onClick={(event) => {
            onClick?.(event);
            if (closeModalOnClick) {
              onClose(buttonProps.name);
            }
          }}
        >
          {text}
        </Button>
      ))}
    </div>
  );
};

function Dialog({
  buttons,
  children,
  className,
  description,
  header,
  id,
  loading = false,
  ref,
  showCloseIcon = true,
  showCloseButton = showCloseIcon,
  size,
  visible = false,
  onClose,
  ...props
}: DialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const onCloseCalledRef = useRef(false);
  const titleId = useId();
  const descriptionId = useId();
  const hasContent = children !== undefined && children !== null && children !== false;

  // Sync native dialog open state with visible prop
  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    // Only make DOM calls when current state doesn't match target state.
    // This prevents race conditions from rapid visible prop changes because
    // we check the actual dialog.open state right before each DOM call.
    if (visible && !dialog.open) {
      dialog.showModal();
      // Reset the flag only during open transition (closed → open).
      // This allows onClose to be called when the dialog actually closes.
      onCloseCalledRef.current = false;
    } else if (!visible && dialog.open) {
      dialog.close();
    }
  }, [visible]);

  const show = () => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) {
      dialog.showModal();
      onCloseCalledRef.current = false;
    }
  };

  const close = useCallback(
    (buttonName?: string) => {
      const dialog = dialogRef.current;
      // The notification is claimed before the dialog closes. A UA that fires the native close
      // event synchronously would otherwise reach handleNativeClose while the flag was still
      // clear, reporting a dismissal alongside the real button name.
      const alreadyNotified = onCloseCalledRef.current;
      onCloseCalledRef.current = true;

      if (dialog?.open) {
        dialog.close();
        // onClose is called immediately so parent state updates without waiting for the native event.
        onClose?.(buttonName);
      } else if (!alreadyNotified) {
        // Already closed, so just notify the parent.
        onClose?.(buttonName);
      }
    },
    [onClose]
  );

  useImperativeHandle(ref, () => ({
    show,
    close,
  }));

  const handleNativeClose = useCallback(
    (event: React.SyntheticEvent<HTMLDialogElement>) => {
      // Without this, closing a nested dialog will also close any parent dialog.
      event.stopPropagation();

      // This fires when the dialog is closed by ANY means (ESC, dialog.close()).
      // A close that carries a button name has already notified the parent from close(), so anything
      // reaching here is a dismissal. It reports 'dismiss' so callers can tell an ESC apart from a
      // programmatic close(), which reaches them with no button name.
      if (!onCloseCalledRef.current) {
        onCloseCalledRef.current = true;
        onClose?.('dismiss');
      }
    },
    [onClose]
  );

  return createPortal(
    <dialog
      data-slot="dialog"
      ref={dialogRef}
      id={id}
      className={cn(dialogVariants({ size, className }))}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      onClose={handleNativeClose}
      {...props}
    >
      {loading && (
        <div className="absolute inset-0 z-10 grid place-content-center rounded-md bg-popover/40">
          <Spinner className="size-6" />
        </div>
      )}
      <DialogHeader>
        <DialogTitle id={titleId}>{header}</DialogTitle>
        {description && <DialogDescription id={descriptionId}>{description}</DialogDescription>}
      </DialogHeader>
      {hasContent && <DialogScrollArea>{children}</DialogScrollArea>}
      <DialogFooter buttons={buttons} showCloseButton={showCloseButton} onClose={close} />
      {showCloseIcon && <DialogCloseButton onClick={() => close('close-icon')} />}
    </dialog>,
    document.body
  );
}

export default Dialog;
